// Single source of truth for the E-mail page's "Nuværende opsætning" rows
// AND their detail overlays (ExchangeSettingDetail) — built once here so
// the clickable list row and the drawer it opens can never describe a
// setting differently.
export const DMARC_POLICY_LABEL = { none: 'Ingen håndhævelse', quarantine: 'Karantæne', reject: 'Reject' };

export function checkedAgo(iso) {
  if (!iso) return 'Ikke tilgængeligt endnu';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'I dag';
  if (days === 1) return 'I går';
  return `For ${days} dage siden`;
}

export function buildExchangeSettings(insights, checkedAt) {
  const spf = insights?.emailAuth?.spf;
  const dmarc = insights?.emailAuth?.dmarc;

  return {
    spf: {
      key: 'spf',
      icon: 'SP',
      title: 'SPF',
      description: 'Kontrollerer hvilke mailservere der må sende på jeres vegne',
      value: spf?.configured ? 'Konfigureret' : 'Ikke konfigureret',
      tone: spf?.configured ? 'good' : 'bad',
      meaning: 'SPF er en liste over de mailservere, der må sende mail fra jeres domæne. Uden den kan en modtager ikke se forskel på jeres mail og forfalskede mails, som kan bruges til at snyde kunder eller kolleger.',
      situation: spf?.configured
        ? 'Jeres SPF-record er konfigureret.'
        : 'Der er ikke fundet en SPF-record for domænet.',
      // Raw record shown in its own code box (ConfigurationPage's .cfg-record), not stuffed into the
      // sentence above — a long v=spf1 string reads much better on its own line, monospaced.
      record: spf?.configured ? spf.record : null,
      recommend: spf?.configured
        ? 'Kontrollér recorden igen, hvis virksomheden tager en ny mailtjeneste i brug.'
        : 'Overvej at tilføje en SPF-record.',
      evidence: [
        ['Observed value', spf?.record || 'Ikke tilgængeligt endnu'],
        ['Source', 'Exchange Online / DNS'],
        ['Collected at', checkedAt],
      ],
    },
    dmarc: {
      key: 'dmarc',
      icon: 'DM',
      title: 'DMARC',
      description: 'Bestemmer hvad modtageren gør med mail, der fejler kontrollen',
      value: dmarc?.configured ? DMARC_POLICY_LABEL[dmarc.policy] || dmarc.policy : 'Ikke konfigureret',
      tone: dmarc?.risk === 'ok' ? 'good' : dmarc?.risk === 'bad' ? 'bad' : 'warn',
      meaning: 'DMARC bestemmer, hvad en modtager skal gøre med mails, der ser ud til at komme fra jer, men som ikke består kontrollen: ingenting, karantæne eller afvisning. Uden DMARC får I heller ingen rapporter om forfalskede mails.',
      situation: dmarc?.configured
        ? `Jeres DMARC-politik er sat til "${DMARC_POLICY_LABEL[dmarc.policy] || dmarc.policy}".`
        : 'Der er ikke fundet en DMARC-record for domænet.',
      record: dmarc?.configured ? dmarc.record : null,
      recommend:
        dmarc?.risk === 'ok'
          ? 'Kontrollér recorden igen, hvis virksomheden tager en ny mailtjeneste i brug.'
          : dmarc?.configured
          ? 'DMARC er sat til kun at overvåge. Når alle jeres egne afsendere er sat op, kan I overveje at sætte den til karantæne eller afvisning.'
          : 'Overvej at tilføje en DMARC-record.',
      evidence: [
        ['Observed value', dmarc?.record || 'Ikke tilgængeligt endnu'],
        ['Source', 'Exchange Online / DNS'],
        ['Collected at', checkedAt],
      ],
    },
    // "Ekstern videresendelse" (a count of mailboxes that forward externally) used to live here — removed:
    // it was a per-mailbox scan, exactly the same computation as Arbejdskø's own exchange-forward finding,
    // not a tenant-wide setting. SPF/DMARC above ARE real settings (Microsoft stores one value for the
    // whole tenant), so they stay.
  };
}
